/**
 * Admin V5 - IELTS V2 Production Restore.
 * Route: /admin/ielts-v2-restore
 *
 * Uses the existing admin cookie session. Idempotent, IELTS-scoped,
 * additive/upsert only. Never touches PTE, Digital SAT, Selective Entry,
 * users, attempts, XP, Course Bookings or Payments.
 *
 * Endpoints (all admin-gated in backend):
 *   GET  /api/ielts/v2/admin/status
 *   POST /api/ielts/v2/admin/deploy-seed?dry_run=true
 *   POST /api/ielts/v2/admin/deploy-seed
 *   GET  /api/ielts/v2/admin/parity
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, CheckCircle2, AlertCircle, PlayCircle, RotateCcw, ShieldCheck } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const ax = axios.create({ baseURL: BACKEND, withCredentials: true });

const LABELS = {
  listening_sim_parts:            'Listening Simulation Parts',
  reading_sim_sections_academic:  'Academic Reading Sections',
  reading_sim_sections_gt:        'General Training Reading Sections',
  writing_tasks_total:            'Writing Prompts (AT1 / AT2 / GT1 / GT2)',
  speaking_p1:                    'Speaking Part 1 Topic Groups',
  speaking_p2:                    'Speaking Part 2 Cue Cards',
  speaking_p3:                    'Speaking Part 3 Discussion Groups',
  practice_listening_sets:        'Practice Listening Sets',
  practice_reading_sets:          'Practice Reading Sets',
  band_configs:                   'Band Conversion Configs',
  mock_variants_academic:         'Full Mock Variants — Academic',
  mock_variants_gt:               'Full Mock Variants — General Training',
  overall_band_rule:              'IELTS Overall Band Rule (four-skill mean)',
};

const ORDER = [
  'listening_sim_parts',
  'practice_listening_sets',
  'reading_sim_sections_academic',
  'reading_sim_sections_gt',
  'practice_reading_sets',
  'writing_tasks_total',
  'speaking_p1',
  'speaking_p2',
  'speaking_p3',
  'mock_variants_academic',
  'mock_variants_gt',
  'band_configs',
  'overall_band_rule',
];

export default function AdminIeltsV2RestorePage() {
  const [status, setStatus] = useState(null);
  const [dryPlan, setDryPlan] = useState(null);
  const [restoreReport, setRestoreReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState([]);

  const push = (t) => setLog((l) => [...l, `[${new Date().toLocaleTimeString()}] ${t}`]);

  const loadStatus = useCallback(async () => {
    try {
      const r = await ax.get('/api/ielts/v2/admin/status');
      setStatus(r.data);
    } catch (e) {
      push(`status failed: ${e?.response?.status || e.message}`);
    }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const runDry = async () => {
    setRunning(true); setDryPlan(null); setRestoreReport(null);
    try {
      push('Running IELTS V2 dry run (no writes)...');
      const r = await ax.post('/api/ielts/v2/admin/deploy-seed?dry_run=true');
      setDryPlan(r.data.report);
      const p = r.data.report;
      const totalInsert =
        (p.listening_simulation?.would_insert || 0) +
        (p.reading_simulation?.would_insert || 0) +
        (p.writing_tasks?.would_insert || 0) +
        (p.speaking_bank?.would_insert || 0) +
        (p.mock_variants?.would_insert || 0) +
        (p.overall_band_rule?.would_insert || 0) +
        (p.band_configs?.would_insert || 0);
      const totalUpdate =
        (p.listening_simulation?.would_update || 0) +
        (p.reading_simulation?.would_update || 0) +
        (p.writing_tasks?.would_update || 0) +
        (p.speaking_bank?.would_update || 0) +
        (p.mock_variants?.would_update || 0) +
        (p.overall_band_rule?.would_update || 0) +
        (p.band_configs?.would_update || 0);
      push(`Dry run complete. Would INSERT ${totalInsert}, UPDATE ${totalUpdate}. Overall parity: ${p.parity?.overall_match}.`);
      await loadStatus();
    } catch (e) {
      push(`FAILED: ${e?.response?.status || e.message} - ${e?.response?.data?.detail || ''}`);
    } finally {
      setRunning(false);
    }
  };

  const runFull = async () => {
    if (!window.confirm(
      'Restore IELTS V2 content into this environment?\n\n' +
      'This is idempotent, additive (upsert) and IELTS-scoped only.\n' +
      'PTE, Digital SAT, Selective Entry, users, attempts, XP, Course Bookings and Payments are NEVER touched.'
    )) return;
    setRunning(true); setRestoreReport(null); setDryPlan(null);
    try {
      push('Restoring IELTS V2 (idempotent, additive)...');
      const r = await ax.post('/api/ielts/v2/admin/deploy-seed');
      const rep = r.data.report;
      setRestoreReport(rep);
      push(`  Listening sim: created ${rep.listening_simulation.created}, updated ${rep.listening_simulation.updated}`);
      push(`  Reading sim: created ${rep.reading_simulation.created}, updated ${rep.reading_simulation.updated}`);
      push(`  Writing tasks: created ${rep.writing_tasks.created}, updated ${rep.writing_tasks.updated}`);
      push(`  Speaking bank: created ${rep.speaking_bank.created}, updated ${rep.speaking_bank.updated}`);
      push(`  Mock variants: created ${rep.mock_variants?.created ?? 0}, updated ${rep.mock_variants?.updated ?? 0}`);
      push(`  Overall band rule: ${rep.overall_band_rule ?? 'unchanged'}`);
      push(`  Practice sets: listening ${rep.listening_practice?.listening ?? 0}, reading ${rep.listening_practice?.reading ?? 0}`);
      push(`  Band configs: ${Object.entries(rep.band_configs).map(([k,v]) => `${k}=${v}`).join(', ')}`);
      push(`Overall parity match: ${rep.parity?.overall_match}`);
      await loadStatus();
    } catch (e) {
      push(`FAILED: ${e?.response?.status || e.message} - ${e?.response?.data?.detail || ''}`);
    } finally {
      setRunning(false);
    }
  };

  const runVerify = async () => {
    setRunning(true);
    try {
      const r = await ax.get('/api/ielts/v2/admin/parity');
      setStatus({ ...(status || {}), ...r.data });
      push(`Parity verified. Overall match: ${r.data.overall_match}`);
    } catch (e) {
      push(`FAILED: ${e?.response?.status || e.message}`);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="admin-ielts-v2-restore">
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-indigo-400 font-bold">Admin V5 &middot; IELTS V2</div>
          <h1 className="text-3xl md:text-4xl font-bold mt-1">IELTS V2 Production Restore</h1>
          <p className="text-slate-400 text-sm mt-1 max-w-3xl">
            Idempotent restore of the Phase 1 IELTS V2 dataset (Listening Simulation parts, Academic + GT Reading, Writing prompts, Speaking bank, practice sets and band-conversion configs) into this environment.
            Additive / upsert only. IELTS-scoped &mdash; never touches PTE, Digital SAT, Selective Entry, users, attempts, XP, Course Bookings or Payments.
          </p>
        </div>

        {/* Scope banner */}
        {status?.scope && (
          <div className="rounded-2xl bg-slate-900 border border-slate-800 p-4 flex items-start gap-3" data-testid="restore-scope">
            <ShieldCheck className="h-5 w-5 text-emerald-400 flex-none mt-0.5" />
            <div className="text-xs text-slate-300">
              <div><span className="text-slate-500">Touched:</span> {status.scope.collections_touched.join(', ')}</div>
              <div><span className="text-slate-500">Never touched:</span> {status.scope.collections_never_touched.join(', ')}</div>
              <div><span className="text-slate-500">Exam scope:</span> exam_type = "{status.scope.exam_type_scope}"</div>
            </div>
          </div>
        )}

        {/* Expected vs current */}
        {status && (
          <div className="rounded-2xl bg-slate-900 border border-slate-800 p-5" data-testid="restore-status">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Expected vs Current</div>
              <div className={`text-xs uppercase tracking-widest font-bold flex items-center gap-1 ${status.overall_match ? 'text-emerald-400' : 'text-amber-400'}`}
                data-testid="restore-overall-match">
                {status.overall_match
                  ? <><CheckCircle2 className="h-3.5 w-3.5" /> Parity match</>
                  : <><AlertCircle className="h-3.5 w-3.5" /> Parity mismatch</>}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
              {ORDER.map((k) => (
                <StatCell key={k}
                  testid={`stat-${k}`}
                  label={LABELS[k] || k}
                  value={status.current?.[k] ?? 0}
                  expected={status.expected?.[k] ?? 0}
                  match={status.matches?.[k]}
                />
              ))}
            </div>
            <div className="mt-4 text-xs text-slate-400">
              <div className="uppercase tracking-widest text-[10px] text-slate-500 mb-1">Band conversion configs present</div>
              <div className="flex flex-wrap gap-2">
                {(status.expected_band_config_keys || []).map((k) => {
                  const has = (status.band_configs_present || []).includes(k);
                  return (
                    <span key={k}
                      data-testid={`bandcfg-${k}`}
                      className={`px-2 py-1 rounded-full text-[11px] ${has ? 'bg-emerald-950 text-emerald-300 border border-emerald-800' : 'bg-amber-950 text-amber-300 border border-amber-800'}`}>
                      {has ? '✓' : '⚠'} {k}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <button onClick={runDry} disabled={running} data-testid="restore-dry"
            className="px-4 py-2 rounded-full bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
            Dry Run
          </button>
          <button onClick={runFull} disabled={running} data-testid="restore-full"
            className="px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
            Restore IELTS V2 (idempotent)
          </button>
          <button onClick={runVerify} disabled={running} data-testid="restore-verify"
            className="px-4 py-2 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Verify Parity
          </button>
          <button onClick={loadStatus} disabled={running} data-testid="restore-refresh"
            className="px-4 py-2 rounded-full bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold inline-flex items-center gap-2">
            <RotateCcw className="h-4 w-4" /> Refresh
          </button>
        </div>

        {/* Dry-run plan */}
        {dryPlan && (
          <div className="rounded-2xl bg-slate-900 border border-slate-800 p-5" data-testid="restore-dryplan">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Dry Run Plan (no writes performed)</div>
            <pre className="text-xs text-slate-300 overflow-x-auto whitespace-pre-wrap" data-testid="restore-dryplan-json">
              {JSON.stringify({
                listening_simulation: dryPlan.listening_simulation,
                reading_simulation: dryPlan.reading_simulation,
                writing_tasks: dryPlan.writing_tasks,
                speaking_bank: dryPlan.speaking_bank,
                mock_variants: dryPlan.mock_variants,
                overall_band_rule: dryPlan.overall_band_rule,
                band_configs: dryPlan.band_configs,
              }, null, 2)}
            </pre>
          </div>
        )}

        {/* Restore report */}
        {restoreReport && (
          <div className={`rounded-2xl border p-5 ${restoreReport.parity?.overall_match ? 'bg-emerald-950 border-emerald-800' : 'bg-amber-950 border-amber-800'}`}
            data-testid="restore-report">
            <div className="text-xs uppercase tracking-widest font-bold mb-2 flex items-center gap-1">
              {restoreReport.parity?.overall_match
                ? <><CheckCircle2 className="h-3.5 w-3.5" /> Restore complete &middot; parity match</>
                : <><AlertCircle className="h-3.5 w-3.5" /> Restore complete &middot; parity mismatch</>}
            </div>
            <pre className="text-xs text-slate-300 overflow-x-auto whitespace-pre-wrap" data-testid="restore-report-json">
              {JSON.stringify(restoreReport, null, 2)}
            </pre>
          </div>
        )}

        {/* Log */}
        <div className="rounded-2xl bg-black border border-slate-800 p-4 h-64 overflow-auto font-mono text-xs text-slate-300" data-testid="restore-log">
          {log.length === 0
            ? <div className="text-slate-600">Log output will appear here...</div>
            : log.map((l, i) => <div key={i}>{l}</div>)}
        </div>
      </div>
    </div>
  );
}

const StatCell = ({ label, value, expected, match, testid }) => {
  const ok = match !== false;
  return (
    <div data-testid={testid}
      className={`rounded-xl border p-3 ${ok ? 'bg-emerald-950/40 border-emerald-800' : 'bg-amber-950/40 border-amber-800'}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
      <div className="text-2xl font-bold mt-0.5">
        {value} <span className="text-xs text-slate-500 font-normal">/ {expected}</span>
      </div>
      <div className={`text-[10px] mt-0.5 ${ok ? 'text-emerald-400' : 'text-amber-400'}`}>
        {ok ? 'OK' : 'BELOW EXPECTED'}
      </div>
    </div>
  );
};
